/**
 * UpgradeModal — the single global paywall popup (driven by useUpgradeModal).
 * Mounted once in V2App; any code can `useUpgradeModal.getState().show(ctx)` to
 * pop it in place. Free-tier creation caps land here instead of redirecting.
 */
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Crown, X, Sparkles } from 'lucide-react';
import { useUpgradeModal, type UpgradeContext } from '@/v2/upgradeModal';
import { trackPaywallCtaClicked } from '@/services/analyticsService';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

function copyFor(ctx: UpgradeContext | null): { title: string; body: string; cta: string; upgradeContext: string } {
  switch (ctx?.kind) {
    case 'free_asset_limit': {
      const noun = ctx.asset === 'avatar'
        ? i18n.t('upgradeModal.nounAvatar', { ns: 'v2' })
        : ctx.asset === 'voice'
          ? i18n.t('upgradeModal.nounVoice', { ns: 'v2' })
          : i18n.t('upgradeModal.nounBrandKit', { ns: 'v2' });
      // a whole plural word per asset: "noun + s" only works in English
      const plural = ctx.asset === 'avatar'
        ? i18n.t('upgradeModal.pluralAvatars', { ns: 'v2' })
        : ctx.asset === 'voice'
          ? i18n.t('upgradeModal.pluralVoices', { ns: 'v2' })
          : i18n.t('upgradeModal.pluralBrandKits', { ns: 'v2' });
      return {
        title: i18n.t('upgradeModal.freeAssetLimitTitle', { ns: 'v2', noun }),
        body: i18n.t('upgradeModal.freeAssetLimitBody', { ns: 'v2', noun, plural }),
        cta: i18n.t('upgradeModal.freeAssetLimitCta', { ns: 'v2' }),
        upgradeContext: `free_${ctx.asset}_limit`,
      };
    }
    case 'free_project_limit':
      return {
        title: i18n.t('upgradeModal.freeProjectLimitTitle', { ns: 'v2' }),
        body: i18n.t('upgradeModal.freeProjectLimitBody', { ns: 'v2' }),
        cta: i18n.t('upgradeModal.freeProjectLimitCta', { ns: 'v2' }),
        upgradeContext: 'free_project_limit',
      };
    case 'insufficient_credits':
      return {
        title: i18n.t('upgradeModal.insufficientCreditsTitle', { ns: 'v2' }),
        body: i18n.t('upgradeModal.insufficientCreditsBody', { ns: 'v2' }),
        cta: i18n.t('upgradeModal.insufficientCreditsCta', { ns: 'v2' }),
        upgradeContext: 'insufficient_credits',
      };
    default:
      return {
        title: i18n.t('upgradeModal.defaultTitle', { ns: 'v2' }),
        body: i18n.t('upgradeModal.defaultBody', { ns: 'v2' }),
        cta: i18n.t('upgradeModal.defaultCta', { ns: 'v2' }),
        upgradeContext: 'pro_feature',
      };
  }
}

export function UpgradeModal() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const open = useUpgradeModal((s) => s.open);
  const context = useUpgradeModal((s) => s.context);
  const hide = useUpgradeModal((s) => s.hide);
  if (!open) return null;
  const c = copyFor(context);
  const existing = context?.kind === 'free_project_limit' ? context.existingOrderId : null;
  // The claim that produced this modal says which backend holds that project.
  const existingProvider = context?.kind === 'free_project_limit' ? context.existingVideoProvider : undefined;
  const dismiss = () => hide();
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in" onClick={dismiss}>
      <div
        className="relative w-full max-w-md rounded-3xl bg-[#15151d] border border-primary/30 p-7 shadow-2xl animate-slide-up text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={dismiss} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-surface border border-glass-border flex items-center justify-center text-text-secondary hover:text-text-primary" aria-label={t('upgradeModal.close')}>
          <X className="w-4 h-4" />
        </button>
        <span className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-glow mb-4">
          <Crown className="w-7 h-7 text-white" />
        </span>
        <h3 className="text-xl font-extrabold text-text-primary">{c.title}</h3>
        <p className="text-sm text-text-secondary leading-relaxed mt-2">{c.body}</p>
        <button
          onClick={() => { trackPaywallCtaClicked('upgrade_modal'); hide(); navigate(`/subscription?context=${c.upgradeContext}`); }}
          className="mt-6 w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-primary to-accent text-white font-bold hover:opacity-95 hover:shadow-glow transition-all"
        >
          <Sparkles className="w-5 h-5" /> {c.cta}
        </button>
        {existing && (
          <button
            onClick={() => { hide(); navigate(`/projects/${existing}`, { state: { videoProvider: existingProvider ?? null } }); }}
            className="mt-2 w-full py-2.5 rounded-xl text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
          >
            {t('upgradeModal.openMyProject')}
          </button>
        )}
        <button onClick={dismiss} className="mt-1 w-full py-2 text-xs text-text-tertiary hover:text-text-secondary transition-colors">
          {t('upgradeModal.maybeLater')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
